/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';
import { IIspSerpData } from './isp-serp.data';
import { IIspSerpProps } from './isp-serp.props.autogenerated';

import dynamic from 'next/dynamic';
const IspLeftContainer = dynamic(() => import('./isp_left_container'), {
  ssr: false,
});
const IspSerpBox = dynamic(() => import('./isp_search_box'), {
    ssr: false,
  });
const IspCenterContainer = dynamic(() => import('./isp_center_container'), {
    ssr: false,
});

import './isp-serp.scss';

export type IIspSerpViewProps = IIspSerpProps<IIspSerpData>;

/**
 * IspOptions settings
 */
class IspOptions {
    public ispEndpoint:string = 'https://ultimate-dot-acp-magento.appspot.com';
    public ispSearchResultsBasePageLoadTime:number = Date.now();
    public ispGlobalSerpScriptsInjected:boolean = false;
    public ispHostPrefix:string | null = '';
    public ispApiPrefix:string | null = '';
  }

/**
 *
 * IspSerp component
 * @extends {React.PureComponent<IIspSerpProps<IIspSerpData>>}
 */
class IspSerp extends React.PureComponent<IIspSerpProps<IIspSerpData>> {
    /* tslint:disable:no-string-literal */
    public componentDidMount():void {
        const options = new IspOptions();
        const { config } = this.props;
        if (options.ispHostPrefix === '') {
            try {
                if (localStorage.getItem('_isp_host_prefix') !== null) {
                    options.ispHostPrefix = localStorage.getItem('_isp_host_prefix');
                } else {
                    options.ispHostPrefix = '';
                }
                if (options.ispHostPrefix) {
                    options.ispApiPrefix = localStorage.getItem('_isp_api_prefix');
                }
            } catch (e) {
                console.log(e);
            }
        }

        window['__isp_serp_uuid'] = config.uuid;
        window['__isp_serp_store_id'] = config.storeId;

        function isp_injectCSS(
            url:string,
            cssId:string,
            beforeId:string,
            rel:string='stylesheet'
            ):void {
            const link  = document.createElement('link');
            link.id   = cssId;
            link.rel  = rel;
            if (rel !== 'preload') {
                link.media = 'all';
                link.type = 'text/css';
            } else {
                link.as = 'script';
            }
            link.href = url;

            if (beforeId !== '') {
                const thisNode = document.getElementById(beforeId);
                if (thisNode !== null && thisNode.parentNode !== null) {
                    thisNode.parentNode.insertBefore(link, thisNode.nextSibling);
                }
            } else {
                const head  = document.getElementsByTagName('head')[0];
                if (head !== null) {
                    head.appendChild(link);
                }
            }
        }

        function isp_injectJS(url:string, beforeId:string):void {
            const s = document.createElement('script');
            s.type = 'text/javascript';
            s.async = false;
            s.src = url;
            const thisNode = document.getElementById(beforeId);
            if (thisNode !== null && thisNode.parentNode !== null) {
                thisNode.parentNode.insertBefore(s, thisNode.nextSibling);
            }
        }

        if (!options.ispGlobalSerpScriptsInjected) {
            isp_injectCSS('https://cdn1-gae-ssl-default.akamaized.net/css/search_result_page_high_fidelity.min.css', 'isp_search_results_css_inject', '', 'stylesheet');
            isp_injectCSS('https://cdn1-gae-ssl-default.akamaized.net/js/search_result_page.min.js?isp_platform=dynamics', 'isp_search_results_css', '', 'preload');
            isp_injectJS('https://ajax.googleapis.com/ajax/libs/jquery/1.12.4/jquery.min.js', 'isp_search_results_js_inject');

            let host 	= 'https://cdn1-gae-ssl-default.akamaized.net';
            let jsFile = '/js/search_result_page.min.js';
            if (options.ispHostPrefix && document !== null) {
                host = options.ispHostPrefix === 'localhost' ? 'https://localhost' :
                                                               `https://${options.ispHostPrefix}-dot-acp-magento.appspot.com`;
                try {
                        const cssContainer = document.getElementById('isp_search_results_css');
                        if (cssContainer !== null) {
                            cssContainer.remove();
                        }
                } catch (e) {
                    console.log(e);
                }
                const theme = '_high_fidelity';
                isp_injectCSS(`${host}/css/search_result_page${theme}.css`, 'isp_search_results_css', 'isp_search_results_css_inject');
                jsFile = '/js/search_result_page.js';
            }
            isp_injectJS(`${host}${jsFile}?isp_platform=dynamics`, 'isp_search_results_js_inject');
            if (History === undefined)	{
                isp_injectJS(`${host}/js/jquery.history.js?v=1.0.6`, 'isp_search_results_js_inject');
            }
            isp_injectJS('https://cdn1-gae-ssl-default.akamaized.net/js/jquery.raty.js?v=2020.07.04a', 'isp_search_results_js_inject');
            isp_injectCSS('https://cdn1-gae-ssl-default.akamaized.net/css/jquery.raty.css?v=1.3.01', 'isp_search_results_css', 'stylesheet');
            options.ispGlobalSerpScriptsInjected = true;
            window['isp_global_serp_scripts_injected'] = options.ispGlobalSerpScriptsInjected;
        }

    }
    /* tslint:enable:no-string-literal */

    public render(): JSX.Element | null {
        return (
            <div className='row'>
                <div id='isp_search_results_js_inject'/>
                <div id='isp_search_results_css_inject'/>

                <div id='isp_search_result_page_container'>
                    <div id='isp_search_results_banner'/>
                    <IspSerpBox/>
                    <IspLeftContainer/>
                    <div id='isp_right_container' className='isp_right_container'/>
                    <IspCenterContainer/>
                </div>
            </div>
        );
    }
}

export default IspSerp;
